import React, { useState, useEffect, useContext, useRef } from 'react';
import './ModuleDetailModal.css';
import Modal from '../Modal/Modal';
import { EnvironmentContext } from '../../contexts/EnvironmentContext';
import { ModuleAdminContext } from '../../contexts/ModuleAdminContext';
import { AdminContext } from '../../contexts/AdminContext';
import baseApi from '../../common/api/base';
import convenienceApi from '../../common/api/convenience';
import ModuleDetailModalContent from './ModuleDetailModalContent';
import logComponent from '../../common/logComponent';

export default function ModuleDetailModal({ visibleModule, hide, groupsLoading, groupsError, onUpdate }) {
  const environmentContext = useContext(EnvironmentContext);
  const adminContext = useContext(AdminContext);
  const moduleAdminContext = useContext(ModuleAdminContext);
  const { activeUser: user, hasToken, selectedMonitoredIndividual, organization } = environmentContext;
  const [currentPanel, setCurrentPanel] = useState(environmentContext.activeUser.groupTypeString === 'Status Solutions' ? 'Availability' : 'Assignment');
  const [enabledForFacility, setEnabledForFacility] = useState(visibleModule.facilityStatus && visibleModule.facilityStatus === 'Enabled');// remember, this will not make sense for Status admins or corporate admins
  const [enabledForCorporation, setEnabledForCorporation] = useState(visibleModule.corporationStatus && visibleModule.corporationStatus === 'Enabled');
  const [enabledForFamily, setEnabledForFamily] = useState(visibleModule.familyStatus && visibleModule.familyStatus === 'Enabled');// remember, this will not make sense for Status admins or corp admins
  const {
    initialGroupTypePermissions,
    currentGroupTypePermissions,
    initialGroupPermissions,
    currentGroupPermissions,
    updateStatus,
    setModule,
    setInitialGroupTypePermissions,
    setCurrentGroupTypePermissions,
    setGroupTypePermissionsLoading,
    setGroupTypePermissionsError,
    setInitialGroupPermissions,
    setCurrentGroupPermissions,
    setGroupPermissionsLoading,
    setGroupPermissionsError,
    setUpdateStatus
  } = moduleAdminContext;


  const [staffCheckNoteSettings, setStaffCheckNoteSettings] = useState({
    familyNoteRequired: null,
    staffNoteRequired: null
  });
  const [hasSettings, setHasSettings] = useState(false);
  const [modalShouldExit, setModalShouldExit] = useState(false);

  const modalRef = useRef(null);



  const setModalRef = (el) => {
    if (logComponent('ModuleDetailModal')) { console.debug('ModuleDetailModal setModalRef'); }
    modalRef.current = el;
  }

  if (logComponent('ModuleDetailModal')) { console.debug('ModuleDetailModal render'); }

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (logComponent('ModuleDetailModal')) { console.debug('ModuleDetailModal onMount'); }
    if (!hasToken) return;
    setModule(visibleModule);

    if (user.groupTypeString !== 'Family') loadGroupPermissions();

  }, [])

  useEffect(() => {
    if (logComponent('ModuleDetailModal')) { console.debug('ModuleDetailModal useEffect [environmentContext.hasToken]'); }
    if (!hasToken || !visibleModule) return;

    if (user.groupTypeString !== 'Family') loadGroupPermissions();
    loadGroupTypePermissions();
  }, [environmentContext.hasToken])

  useEffect(() => {
    if (logComponent('ModuleDetailModal')) { console.debug('ModuleDetailModal useEffect [updateStatus]'); }
    // handle submission failure
    if (!hasToken) return;
    if (modalRef.current != null)
      modalRef.current.scrollTop = 0;
  }, [updateStatus])

  const loadGroupPermissions = () => {
    if (logComponent('ModuleDetailModal')) { console.debug('ModuleDetailModal loadGroupPermissions'); }
    if (!hasToken || !visibleModule) return;

    setGroupPermissionsLoading(true);
    setGroupPermissionsError(false);

    convenienceApi.getModuleGroupPermissions(visibleModule)
      .then(groups => {
        setInitialGroupPermissions(groups.map(group => group.groupId));
        setCurrentGroupPermissions(groups.map(group => group.groupId));
        setGroupPermissionsLoading(false);
        setGroupPermissionsError(false);
      },
        error => {
          setGroupPermissionsLoading(false);
          setGroupPermissionsError(true);
        });
  }

  const loadGroupTypePermissions = () => {
    if (logComponent('ModuleDetailModal')) { console.debug('ModuleDetailModal loadGroupTypePermissions'); }
    if (!hasToken || !visibleModule) return;

    setGroupTypePermissionsLoading(true);
    setGroupTypePermissionsError(false);

    baseApi.Module.GetGroupTypePermissions(visibleModule.moduleId)
      .then(result => {
        const permissions = result.data;
        setInitialGroupTypePermissions(permissions);
        setCurrentGroupTypePermissions(permissions);
        setGroupTypePermissionsLoading(false);
        setGroupTypePermissionsError(false);
      },
        error => {
          setGroupTypePermissionsLoading(false);
          setGroupTypePermissionsError(true);
        });
  }

  const toggleGroupTypePermissions = (groupTypeId) => {
    if (logComponent('ModuleDetailModal')) { console.debug('ModuleDetailModal toggleGroupTypePermissions'); }
    let remove = currentGroupTypePermissions.includes(groupTypeId);
    let newPermissions;
    if (remove) {
      newPermissions = currentGroupTypePermissions.filter(permission => permission !== groupTypeId);
    } else {
      newPermissions = currentGroupTypePermissions.concat(groupTypeId);
    }
    setCurrentGroupTypePermissions(newPermissions);

  }

  const toggleFacilityEnabledStatus = () => {
    if (logComponent('ModuleDetailModal')) { console.debug('ModuleDetailModal toggleFacilityEnabledStatus'); }
    setEnabledForFacility(!enabledForFacility);
  }

  const toggleFamilyEnabledStatus = () => {
    if (logComponent('ModuleDetailModal')) { console.debug('ModuleDetailModal toggleFamilyEnabledStatus'); }
    setEnabledForFamily(!enabledForFamily);
  }

  const toggleCorporationEnabledStatus = () => {
    if (logComponent('ModuleDetailModal')) { console.debug('ModuleDetailModal toggleCorporationEnabledStatus'); }
    setEnabledForCorporation(!enabledForCorporation);
  }

  const updatePanel = (panelTitle) => {
    if (logComponent('ModuleDetailModal')) { console.debug('ModuleDetailModal updatePanel'); }
    setCurrentPanel(panelTitle);
  }

  const updateModule = () => {
    if (logComponent('ModuleDetailModal')) { console.debug('ModuleDetailModal updateModule'); }
    if (!hasToken) return;

    switch (user.groupTypeString) {
      case 'Status Solutions':
        updateModuleGroupTypeAndGroupPermissions();
        break;
      case 'Corporate':
        updateModuleForCorporation();
        break;
      case 'Facility':
        updateModuleForFacility();
        break;
      default:
        break;
    }


    const selectedMonitoredIndividualId = selectedMonitoredIndividual ? selectedMonitoredIndividual.monitoredIndividualId : null;
    if (logComponent('ModuleDetailModal')) { console.debug('ModuleDetailModal selectedMonitoredIndividualId'); }
    baseApi.Module.GetAllForCurrentUser(selectedMonitoredIndividualId)
      .then(result => {
        environmentContext.setUserModules(result.data);
      });

  }

  const updateModuleForFacility = () => {
    if (logComponent('ModuleDetailModal')) { console.debug('ModuleDetailModal updateModuleForFacility'); }
    if (!hasToken) return;


    // construct new module status to pass to api
    let updatedModule = {
      facilityStatus: visibleModule.facilityStatus === 'None' || visibleModule.facilityStatus === 'Not Available' ? visibleModule.facilityStatus : (enabledForFacility ? 'Enabled' : 'Disabled'),
      familyStatus: visibleModule.familyStatus === 'None' || visibleModule.facilityStatus === 'Not Available' ? visibleModule.familyStatus : (enabledForFamily ? 'Enabled' : 'Disabled'),
      groupPermissions: currentGroupPermissions
    }

    visibleModule.groupPermissions = initialGroupPermissions;

    setUpdateStatus('pending');

    convenienceApi.updateModuleForFacility(organization, visibleModule, updatedModule, staffCheckNoteSettings)
      .then(
        result => {
          setUpdateStatus('success');

          // assign updatedModule's FamilyStatus & FacilityStatus to Module
          visibleModule.familyStatus = updatedModule.familyStatus;
          visibleModule.facilityStatus = updatedModule.facilityStatus;

          // TODO: In future, don't modify this directly, but do it through Environment Context
          if (staffCheckNoteSettings.staffNoteRequired !== null) organization.isStaffCheckHistoryStaffNoteRequired = staffCheckNoteSettings.staffNoteRequired;
          if (staffCheckNoteSettings.familyNoteRequired !== null) organization.isStaffCheckHistoryFamilyNoteRequired = staffCheckNoteSettings.familyNoteRequired;


          if (onUpdate) onUpdate(visibleModule);
          setModule(visibleModule);
          environmentContext.setShouldUpdateModules(true);

          startModalExit();
        },
        error => {
          setUpdateStatus('failure');
        });
  }

  const updateModuleForCorporation = () => {
    if (logComponent('ModuleDetailModal')) { console.debug('ModuleDetailModal updateModuleForCorporation'); }
    if (!hasToken) return;

    // construct new module status to pass to api
    let updatedModule = {
      corporationStatus: enabledForCorporation ? 'Enabled' : 'Disabled',
      groupPermissions: currentGroupPermissions

    }

    setUpdateStatus('pending');

    visibleModule.groupPermissions = initialGroupPermissions;

    convenienceApi.updateModuleForCorporation(user.corporationId, visibleModule, updatedModule)
      .then(
        result => {
          setUpdateStatus('success');

          // assign updatedModule's CorporationStatus to Module
          visibleModule.corporationStatus = updatedModule.corporationStatus;

          if (onUpdate) onUpdate(visibleModule);
          setModule(visibleModule);

          // TODO: Uncomment if Corporate users are given access to MonitoredIndividualUnitSearch
          // environmentContext.setShouldUpdateModules(true);

          startModalExit();
        },
        error => {
          setUpdateStatus('failure');
        }
      );

  }

  const updateModuleGroupTypeAndGroupPermissions = () => {
    if (logComponent('ModuleDetailModal')) { console.debug('ModuleDetailModal updateModuleGroupTypeAndGroupPermissions'); }
    if (!hasToken) return;


    setUpdateStatus('pending');


    convenienceApi.updateModuleGroupTypeAndGroupPermissions(visibleModule, initialGroupTypePermissions, currentGroupTypePermissions, initialGroupPermissions, currentGroupPermissions)
      .then(
        result => {
          setUpdateStatus('success');

          const updatedModule = { ...visibleModule };
          updatedModule.isStatus = currentGroupTypePermissions.includes(1);
          updatedModule.isCorporate = currentGroupTypePermissions.includes(2);
          updatedModule.isFacility = currentGroupTypePermissions.includes(3);
          updatedModule.isFamily = currentGroupTypePermissions.includes(4);


          if (onUpdate) onUpdate(updatedModule);
          setModule(updatedModule);

          // TODO: Uncomment if Corporate users are given access to MonitoredIndividualUnitSearch
          // environmentContext.setShouldUpdateModules(true);

          startModalExit();
        },
        error => {
          setUpdateStatus('failure');
        });
  }

  const startModalExit = () => {
    if (logComponent('ModuleDetailModal')) { console.debug('ModuleDetailModal startModalExit'); }
    setModalShouldExit(true);
  }

  const validateField = (arg) => {
    if (logComponent('ModuleDetailModal')) { console.debug('ModuleDetailModal validateField'); }
    setCurrentGroupPermissions(arg.value.map(group => group.groupId));
  }



  const handleStaffCheckNoteChange = (newSetting) => {
    if (logComponent('ModuleDetailModal')) { console.debug('ModuleDetailModal handleStaffCheckNoteChange'); }
    if (newSetting.name === 'StaffNote') {
      let newStaffCheckNoteSettings = { staffNoteRequired: newSetting.text === 'Required' ? true : false }
      setStaffCheckNoteSettings(Object.assign(staffCheckNoteSettings, newStaffCheckNoteSettings));
    } else if (newSetting.name === 'FamilyNote') {
      let newStaffCheckNoteSettings = { familyNoteRequired: newSetting.text === 'Required' ? true : false }
      setStaffCheckNoteSettings(Object.assign(staffCheckNoteSettings, newStaffCheckNoteSettings));
    }
  }

  return (
    <Modal isVisible={true} shouldExit={modalShouldExit} hide={hide} ref={setModalRef} remainOnBackdropClick={true}>
      <ModuleDetailModalContent
        currentPanel={currentPanel}
        toggleGroupTypePermissions={toggleGroupTypePermissions}
        toggleFacilityEnabledStatus={toggleFacilityEnabledStatus}
        toggleCorporationEnabledStatus={toggleCorporationEnabledStatus}
        toggleFamilyEnabledStatus={toggleFamilyEnabledStatus}
        enabledForFamily={enabledForFamily}
        enabledForFacility={enabledForFacility}
        enabledForCorporation={enabledForCorporation}
        groupsLoading={groupsLoading}
        groupsError={groupsError}
        validateField={validateField}
        startModalExit={startModalExit}
        handleStaffCheckNoteChange={handleStaffCheckNoteChange}
        updateModule={updateModule}
      />

    </Modal>
  );
}

